import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Link,
  Loader,
  Snackbar,
  Typography,
} from '@la/ds-ui-components';
import {
  getSiteId,
  getUserId,
  uploadCustomFileField,
  useAppSelector,
} from '@la/services';
import { FormFieldCategory } from '@la/types';
import {
  getLAHostnameParts,
  isTeamPrimaryStaff,
  mapFormFieldToWorkflow,
  removeDuplicateFormFields,
} from '@la/utilities';
import ErrorCard from 'components/ErrorCard/ErrorCard';
import { useGetFormFieldsQuery } from 'redux/services/formFieldsApi';
import { useGetRegistrationSettingsQuery } from 'redux/services/registrationApi';
import {
  useGetTeamQuery,
  useGetNGTeamQuery,
  useUpdateTeamMutation,
  useUpdateFacadeTeamMutation,
} from 'redux/services/teamApi';
import { useGetUserRegistrationsQuery } from 'redux/services/tournamentApi';
import MainContent from 'domains/Shell/MainContent/MainContent';
import Page from 'domains/Shell/Page/Page';
import PageTitle from 'domains/Shell/PageTitle/PageTitle';
import {
  CreateTeamForm,
  CreateTeamFormFields,
  formatTeamForForm,
} from 'domains/Tournaments/Registration/Wizard/components/CreateTeamForm/CreateTeamForm';
import styles from './UpdateTeam.module.scss';

export const API_ERROR =
  'There was an error loading this team. Please try again in a few seconds.';

export const UPDATE_TEAM_FORM_ID = 'updateTeam';

export function UpdateTeam() {
  const { programId, programType, teamId } = useParams();
  const siteId = useAppSelector(getSiteId);
  const userId = useAppSelector(getUserId);
  const { subdomain } = getLAHostnameParts();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);

  if (!programType) {
    throw new Error('No program type provided.');
  }

  if (!programId) {
    throw new Error('No program id provided.');
  }

  if (!teamId) {
    throw new Error('No team id provided.');
  }

  const {
    data: registrationOptions,
    isError: hasRegistrationsOptionsError,
    isLoading: isRegistrationOptionsLoading,
  } = useGetRegistrationSettingsQuery({
    programId,
    subdomain,
  });

  const {
    data: formFields,
    isError: hasFormFieldsError,
    isLoading: isFormFieldsLoading,
  } = useGetFormFieldsQuery({
    category: FormFieldCategory.Team,
    programId,
    siteDomain: subdomain,
  });

  const {
    data: facadeTeam,
    isError: hasFacadeTeamError,
    isLoading: isFacadeTeamLoading,
  } = useGetTeamQuery({
    siteDomain: subdomain,
    teamId,
  });

  const {
    data: ngTeam,
    isError: hasNGTeamError,
    isLoading: isNGTeamLoading,
  } = useGetNGTeamQuery(
    {
      siteDomain: subdomain,
      teamId: facadeTeam?.ngTeamId || '',
    },
    {
      skip: !facadeTeam?.ngTeamId,
    }
  );

  const {
    data: userRegistrations,
    isError: hasUserRegistrationsError,
    isLoading: isUserRegistrationsLoading,
  } = useGetUserRegistrationsQuery({
    siteDomain: subdomain,
    programId,
  });

  const [updateTeam] = useUpdateTeamMutation();
  const [updateFacadeTeam] = useUpdateFacadeTeamMutation();

  if (
    isRegistrationOptionsLoading ||
    isFormFieldsLoading ||
    isFacadeTeamLoading ||
    isNGTeamLoading ||
    isUserRegistrationsLoading
  ) {
    return (
      <Page>
        <UpdateTeamHeader />
        <MainContent centerContent>
          <Loader loading />
        </MainContent>
      </Page>
    );
  }

  if (
    !registrationOptions ||
    !formFields ||
    !facadeTeam ||
    !ngTeam ||
    !userRegistrations ||
    hasRegistrationsOptionsError ||
    hasFormFieldsError ||
    hasFacadeTeamError ||
    hasNGTeamError ||
    hasUserRegistrationsError
  ) {
    return (
      <Page>
        <UpdateTeamHeader />
        <MainContent>
          <ErrorCard message={API_ERROR} />
        </MainContent>
      </Page>
    );
  }

  const team = {
    ...facadeTeam,
    formFields: ngTeam?.metadata?.formFields || [],
  };

  const handleDiscardChanges = (): void => {
    window.history.back();
  };

  const handleUpdateTeam = async (
    updatedTeam: CreateTeamFormFields
  ): Promise<void> => {
    if (!allowUpdating) {
      return;
    }

    setIsLoading(true);

    const {
      customTeamFields,
      province,
      repEmail,
      repName,
      repPhoneNumber,
      state,
      teamName,
      ...restTeam
    } = updatedTeam;
    const admin1: string | undefined = state || province;

    const formattedFields = await uploadCustomFileField({
      fields: customTeamFields,
      siteId,
      userId,
    });
    const updatedFormFields = formattedFields.map(mapFormFieldToWorkflow);
    const allFormFields = removeDuplicateFormFields(
      updatedFormFields.concat(team.formFields ?? [])
    );

    await Promise.all([
      updateTeam({
        siteDomain: subdomain,
        programId,
        team: {
          ...restTeam,
          id: ngTeam.id,
          name: teamName,
          admin1,
          metadata: {
            version: '1.0',
            teamRepresentatives:
              repName && repPhoneNumber && repEmail
                ? [
                    {
                      name: repName,
                      phoneNumber: repPhoneNumber,
                      email: repEmail,
                    },
                  ]
                : undefined,
            ageGroup: restTeam.ageGroup,
            formFields: allFormFields,
          },
        },
      }).unwrap(),
      updateFacadeTeam({
        siteDomain: subdomain,
        team: {
          ...facadeTeam,
          ...restTeam,
          external: !!team.ngTeamId,
          name: teamName,
          ngTeamId: team.ngTeamId,
          admin1,
        },
      }).unwrap(),
    ])
      .then(() => {
        setShowSuccessSnackbar(true);
      })
      .catch(() => {
        setShowErrorSnackbar(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const teamConditionalPricingFormFieldId = registrationOptions.settings.find(
    (setting) => setting.settingName === 'team_conditional_pricing'
  )?.value?.formFieldId;

  const allowUpdating = isTeamPrimaryStaff(
    userRegistrations.userRegistrations,
    Number.parseInt(programId),
    userId,
    Number.parseInt(teamId)
  );

  const formTeam: CreateTeamFormFields = formatTeamForForm(team, formFields);

  return (
    <Page>
      <UpdateTeamHeader />
      <MainContent>
        <div className={styles.updateTeam}>
          <div className={styles.programDetails}>
            {team.groupedProgramName ? (
              <>
                <Link href={`/app/${programType}/${team.groupedProgramId}`}>
                  {team.groupedProgramName}
                </Link>{' '}
                /{' '}
              </>
            ) : null}
            <Link
              href={`/app/${programType}/${team.groupedProgramId || programId}`}
            >
              {team.programName}
            </Link>
          </div>
          <Typography size="large" variant="headline">
            {formTeam.teamName}
          </Typography>
          <CreateTeamForm
            customFields={formFields}
            defaultValues={formTeam}
            existingFields={team.formFields}
            id={UPDATE_TEAM_FORM_ID}
            maxDivisionAgeGroup={0}
            onSubmit={handleUpdateTeam}
            readOnly={!allowUpdating}
            readOnlyCustomFields={
              teamConditionalPricingFormFieldId
                ? { [teamConditionalPricingFormFieldId]: true }
                : undefined
            }
            readOnlyFields={{ ageGroup: true }}
            showSubtitle={false}
          />
          {allowUpdating ? (
            <div className={styles.actions}>
              <Button
                onClick={handleDiscardChanges}
                size="large"
                variant="outline"
              >
                Discard changes
              </Button>
              <Button
                disabled={isLoading}
                form={UPDATE_TEAM_FORM_ID}
                loading={isLoading}
                size="large"
                type="submit"
              >
                Update team
              </Button>
            </div>
          ) : null}
          <Snackbar
            description="There was an error updating this team. Please try again in a few seconds."
            onOpenChange={setShowErrorSnackbar}
            open={showErrorSnackbar}
            variant="error"
          />
          <Snackbar
            description="Team updated successfully."
            onOpenChange={setShowSuccessSnackbar}
            open={showSuccessSnackbar}
          />
        </div>
      </MainContent>
    </Page>
  );
}

function UpdateTeamHeader() {
  return <PageTitle>View your team registration</PageTitle>;
}
